import React, { useState } from "react";
import FormInput from "../../../../form-input/form-input.component";
import loginFormData from "../../../../../../pages/pre-auth/login/forms/login.forms";
import formValidityCheck from "../../../../../utils/validator";
import AppService from "../../../../../services/app-service";
import { ucsfLoginInstance, ucsfInstance } from "../../../../../axios/ucsf";
import { COMPONENT_APIS } from "../../../../../utils/omi-api";
import { useSelector, useDispatch } from "react-redux";
import { setCurrentUser } from "../../../../../../redux/user/user.actions";
import { setLoader } from "../../../../../../redux/app/app.actions";
import { getAppId } from "../../../../../../redux/app/app.selectors";
import SecurityService from "../../../../../services/security-service";
import AuthService from "../../../../../services/authentication-service";
import { trackLoginSuccess } from "../../../../../utils/analytics";

const LoginFlow = ({ onViewChange, history }) => {
  const dispatch = useDispatch();
  const appId = useSelector(getAppId);
  const [userCredentials, setUserCredentials] = useState({
    loginFormElement: { ...loginFormData },
    formIsValid: false,
    loading: false,
  });
  const [loginData, setLoginData] = useState({
    forgotPassword: false,
    passwordSent: false,
    loginError: false,
    loginErrorMessage:
      "Invalid login. Make sure your username and password are correct.",
  });

  const { loginFormElement, formIsValid, loading } = userCredentials;
  const { forgotPassword, passwordSent, loginError, loginErrorMessage } =
    loginData;

  const inputChangedHandler = (event, identifier) => {
    let formIsValid = true;
    const updatedLoginForm = { ...loginFormElement };
    const updatedFormElement = { ...updatedLoginForm[identifier] };
    updatedFormElement.value = event.target.value;
    const { validity, errorMessage } = formValidityCheck(
      updatedFormElement,
      updatedLoginForm,
    );
    updatedFormElement.valid = validity;
    if (updatedFormElement.validation)
      updatedFormElement.validation.message = errorMessage;
    updatedFormElement.touched = true;
    updatedLoginForm[identifier] = updatedFormElement;
    if (forgotPassword) {
      formIsValid = updatedLoginForm["email"].valid;
    } else {
      formIsValid =
        updatedLoginForm["userName"].valid &&
        updatedLoginForm["password"].valid;
    }
    setUserCredentials({
      ...userCredentials,
      loginFormElement: updatedLoginForm,
      formIsValid,
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    AppService.clearAllCookies();
    localStorage.clear();
    dispatch(setLoader(true, "Please wait"));

    if (forgotPassword) {
      const email = loginFormElement["email"]["value"];
      ucsfLoginInstance
        .post(COMPONENT_APIS.EMAIL_VERIFICATION_FOR_FORGOT_PWD, { email })
        .then(() => {
          dispatch(setLoader(false));
          AppService.setNotification({
            title: "Reset link sent!",
            message: `A password reset link has been sent to the email that you've provided. Please click on the link inside to create new password.`,
            type: "success",
            duration: 3000,
          });
          setLoginData({ ...loginData, passwordSent: true });
        })
        .catch((err) => {
          const errMsg = err?.response?.data?.error;
          setLoginData({
            ...loginData,
            loginError: true,
            loginErrorMessage:
              errMsg || "Something went wrong. Please try again.",
          });
          dispatch(setLoader(false));
        });
    } else {
      const username = loginFormElement["userName"]["value"];
      const password = AppService.encryptData(
        loginFormElement["password"]["value"],
      );
      ucsfLoginInstance
        .post(COMPONENT_APIS.USER_LOGIN, { username, password, appid: appId })
        .then((response) => {
          const userData = response.data;
          const userId = userData?.user?.id;
          trackLoginSuccess(userId, "password");
          SecurityService.setSecurityToken(userData.token);
          AuthService._setUser(userData);
          dispatch(setLoader(true, "Logging you in"));
          ucsfInstance
            .get(COMPONENT_APIS.USER_PERMISSION)
            .then((res) => {
              const { permissions, role } = res.data;
              AuthService._setUser({ ...userData, permissions, role });
              dispatch(setCurrentUser({ ...userData, permissions, role }));
              dispatch(setLoader(false));
              history.push("/home");
            })
            .catch(() => {
              dispatch(setLoader(false));
            });
        })
        .catch((error) => {
          const loginErrorMsg = error?.response?.data?.error;
          setLoginData({
            ...loginData,
            loginError: true,
            loginErrorMessage: loginErrorMsg || loginErrorMessage,
          });
          dispatch(setLoader(false));
        });
    }
  };

  return (
    <>
      <div className="auth-card-header">
        <h2 className="auth-card-title">
          {forgotPassword ? "Reset Password" : "Login"}
        </h2>
        <p className="auth-card-subtitle">
          {forgotPassword
            ? "Enter your email to receive a reset link"
            : "Sign in to your account"}
        </p>
      </div>

      {loginError && !forgotPassword && (
        <div className="auth-error-message">{loginErrorMessage}</div>
      )}

      {passwordSent && (
        <div className="auth-success-message">
          Please check your email for the password reset link.
        </div>
      )}

      {!passwordSent && (
        <form onSubmit={handleSubmit} className="auth-card-form">
          <div className="auth-card-content">
            {!forgotPassword && (
              <div className="form-group">
                <label className="form-label">Username</label>
                <FormInput
                  elementType={loginFormElement.userName.elementType}
                  elementConfig={loginFormElement.userName.elementConfig}
                  value={loginFormElement.userName.value}
                  changed={(event) => inputChangedHandler(event, "userName")}
                  touched={loginFormElement.userName.touched}
                  invalid={!loginFormElement.userName.valid}
                  shouldValidate={loginFormElement.userName.validation}
                  errorMessage={loginFormElement.userName.validation?.message}
                />
              </div>
            )}

            {forgotPassword && (
              <div className="form-group">
                <label className="form-label">Email</label>
                <FormInput
                  elementType={loginFormElement.email.elementType}
                  elementConfig={loginFormElement.email.elementConfig}
                  value={loginFormElement.email.value}
                  changed={(event) => inputChangedHandler(event, "email")}
                  touched={loginFormElement.email.touched}
                  invalid={!loginFormElement.email.valid}
                  shouldValidate={loginFormElement.email.validation}
                  errorMessage={loginFormElement.email.validation?.message}
                />
              </div>
            )}

            {!forgotPassword && (
              <div className="form-group">
                <label className="form-label">Password</label>
                <FormInput
                  elementType={loginFormElement.password.elementType}
                  elementConfig={loginFormElement.password.elementConfig}
                  value={loginFormElement.password.value}
                  changed={(event) => inputChangedHandler(event, "password")}
                  touched={loginFormElement.password.touched}
                  invalid={!loginFormElement.password.valid}
                  shouldValidate={loginFormElement.password.validation}
                  errorMessage={loginFormElement.password.validation?.message}
                />
              </div>
            )}

            <div className="auth-card-button-group">
              
              <button
                className="auth-card-button primary"
                onClick={handleSubmit}
                type="submit"
                disabled={!formIsValid || loading}
              >
                {forgotPassword ? "Send Reset Link" : "Login"}{" "}
              </button>
            </div>

            {!forgotPassword && (
              <button
                type="button"
                className="auth-card-link"
                onClick={() =>
                  setLoginData({ ...loginData, forgotPassword: true })
                }
              >
                Forgot your password?
              </button>
            )}

            {forgotPassword && (
              <button
                type="button"
                className="auth-card-link"
                onClick={() => {
                  setLoginData({
                    ...loginData,
                    forgotPassword: false,
                    passwordSent: false,
                  });
                  setUserCredentials({
                    ...userCredentials,
                    formIsValid: false,
                  });
                }}
              >
                Back to Login
              </button>
            )}
          </div>
        </form>
      )}

      <div className="auth-card-footer-links">
        <span className="auth-card-text">
          {forgotPassword ? "Don't have an account?" : "Don't have an account?"}{" "}
          <button
            type="button"
            className="auth-card-link"
            onClick={() => onViewChange("register")}
          >
            Sign Up
          </button>
        </span>
      </div>
    </>
  );
};

export default LoginFlow;
